<template>
  <formulate-form v-slot="{ hasErrors }" class="column content-box">
    <woot-modal-header
      :header-title="
        isContacts || isPhonesMode
          ? $t('CHATLYN_CONTACT_LISTS.SEND_MESSAGE_MODAL.TITLE')
          : $t('CHATLYN_MESSAGE_TEMPLATE.SEND_MODAL.TITLE', {
              TEMPLATE_NAME: messageTemplate.title,
            })
      "
    />

    <div class="modal-content send-message-template-modal-body">
      <div
        v-if="!isAllTranslationsApproved"
        class="callout warning mb-3 flex items-center gap-2 p-3"
      >
        <fluent-icon class="flex-shrink-0" icon="warning" :size="16" />
        <span>
          {{
            $t(
              'CHATLYN_MESSAGE_TEMPLATE.SEND_MODAL.NOT_ALL_TRANSLATIONS_APPROVED_MESSAGE'
            )
          }}
        </span>
      </div>

      <div
        v-if="isShowWarning"
        class="message-template-10-minutes-warning-message callout warning mb-3 flex items-start gap-2 p-3"
      >
        <fluent-icon class="mt-1 flex-shrink-0" icon="warning" :size="16" />
        <div class="message-template-10-minutes-warning-message__content">
          <div class="text-base font-semibold">
            {{
              $t('CHATLYN_MESSAGE_TEMPLATE.SEND_MODAL.10_MINUTES_WARNING.TITLE')
            }}
          </div>
          <div class="text-sm">
            {{
              $t(
                'CHATLYN_MESSAGE_TEMPLATE.SEND_MODAL.10_MINUTES_WARNING.MESSAGE'
              )
            }}
          </div>
        </div>
      </div>

      <div class="grid gap-8 md:grid-cols-2 lg:grid-cols-5">
        <div class="col-span-1 flex flex-col gap-3 lg:col-span-3">
          <template v-if="isContacts || isPhonesMode">
            <multiselect-ch
              ref="templateSelect"
              v-model="template"
              :title="$t('CHATLYN_CONTACT_LISTS.SEND_MESSAGE_MODAL.TEMPLATE')"
              :options="availableMessageTemplates"
              :multiple="false"
              label="title"
              :max-height="140"
              :internal-search="!shouldUseRemoteTemplates"
              :is-loading="isTemplateDropdownLoading"
              @open="handleTemplateDropdownOpen"
              @close="handleTemplateDropdownClose"
              @search-change="handleTemplateSearch"
            >
              <template #title>
                <span class="mr-1 flex items-center">
                  {{ $t('CHATLYN_CONTACT_LISTS.SEND_MESSAGE_MODAL.TEMPLATE') }}
                </span>

                <fluent-icon
                  v-tooltip.top="
                    $t(
                      'CHATLYN_CONTACT_LISTS.SEND_MESSAGE_MODAL.TEMPLATE_TOOLTIP'
                    )
                  "
                  icon="info"
                  size="16"
                />
              </template>
            </multiselect-ch>
          </template>

          <template v-else>
            <multiselect-ch
              v-model="contactList"
              :title="$t('CHATLYN_MESSAGE_TEMPLATE.SEND_MODAL.SELECT_LIST')"
              :options="contactLists"
              :multiple="false"
              label="name"
              :max-height="200"
            >
              <template #title>
                <label class="mr-1 flex items-center">
                  {{ $t('CHATLYN_MESSAGE_TEMPLATE.SEND_MODAL.SELECT_LIST') }}
                </label>
              </template>
            </multiselect-ch>
          </template>

          <div
            v-if="!hasLinks"
            class="message-template-scheduled-container"
            style="gap: 0; margintop: 0"
          >
            <div class="flex flex-row flex-nowrap items-center gap-2">
              <span>
                {{ $t('CHATLYN_MESSAGE_TEMPLATE.SEND_MODAL.RECIPIENTS') }}
              </span>

              <fluent-icon
                v-if="contactList && contactList.name"
                v-tooltip.top="
                  $t(
                    'CHATLYN_CONTACT_LISTS.SEND_MESSAGE_MODAL.RECIPIENTS_TOOLTIP',
                    { list: contactList.name }
                  )
                "
                icon="info"
                size="16"
              />
            </div>

            <div class="text-4xl font-light dark:text-white">
              {{ contactsCount }}
            </div>

            <total-count-warning v-if="showTotalCountWarning" />

            <send-message-limit-warning
              v-if="isSendMessageLimitShown"
              class="mt-0.5"
              :current-sending-limits="currentSendingLimits"
            />
          </div>

          <div class="message-template-scheduled-container !gap-0">
            <div class="message-template-scheduled-switcher">
              <woot-switch
                id="message-template-scheduled-switcher"
                v-model="showSchedule"
                :label="$t('CHATLYN_CONTACT_LISTS.SEND_MESSAGE_MODAL.SCHEDULE')"
              />
            </div>

            <slide-animation :is-shown="showSchedule">
              <div
                v-if="showSchedule"
                class="schedule mt-3 flex w-full flex-wrap gap-x-4 gap-y-2"
              >
                <FormulateInput
                  v-model="scheduleDate"
                  class="flex-1"
                  type="date"
                  :label="$t('CHATLYN_CONTACT_LISTS.SEND_MESSAGE_MODAL.DATE')"
                  placeholder="$t('CHATLYN_CONTACT_LISTS.SEND_MESSAGE_MODAL.DATE')"
                  :min="minDate"
                  :validation="
                    minDate ? `required|after:${afterDate}` : 'required'
                  "
                />

                <FormulateInput
                  v-model="scheduleTime"
                  class="flex-1"
                  type="time"
                  :label="$t('CHATLYN_CONTACT_LISTS.SEND_MESSAGE_MODAL.TIME')"
                  placeholder="$t('CHATLYN_CONTACT_LISTS.SEND_MESSAGE_MODAL.TIME')"
                  :validation="
                    minTime ? `required|timeAfter:${minTime}` : 'required'
                  "
                  :validation-messages="{
                    timeAfter: $t(
                      'CHATLYN_GENERAL.MESSAGES.AFTER_TIME_INVALID',
                      {
                        time: minTime,
                      }
                    ),
                  }"
                  :validation-rules="{
                    timeAfter: (event) => timeAfter(event, minTime),
                  }"
                  :min="minTime"
                />
                <div class="w-full text-xs txt-muted">{{ timezoneTitle }}</div>
              </div>
            </slide-animation>
          </div>

          <div
            v-if="isSpamConfirmRequired"
            class="callout warning flex flex-col gap-2 rounded p-2 text-xs"
          >
            <span
              v-dompurify-html="
                $t('CHATLYN_MESSAGE_TEMPLATE.SEND_MODAL.SPAM_WARNING.MESSAGE', {
                  LINK: 'https://www.whatsapp.com/legal/messaging-guidelines',
                })
              "
            />

            <label class="flex flex-row items-start gap-2">
              <input
                v-model="spamConfirmChecked"
                type="checkbox"
                class="checkbox mt-0.5 shrink-0"
              />
              <span class="font-medium">
                {{
                  $t(
                    'CHATLYN_MESSAGE_TEMPLATE.SEND_MODAL.SPAM_WARNING.CONFIRM_LABEL'
                  )
                }}
              </span>
            </label>
          </div>

          <template v-if="template && template.translations && hasLinks">
            <div class="message-template-links-message callout warning">
              <span class="whitespace-nowrap p-0.5">{ }</span>
              <span>
                {{ $t('CHATLYN_MESSAGE_TEMPLATE.SEND_MODAL.LINKS_MESSAGE') }}
              </span>
            </div>

            <div
              v-if="template && template.translations"
              class="message-template-scheduled-container pt-3"
            >
              <div
                v-show="template.translations.length > 1"
                class="rounded-lg p-3 transition-colors duration-200"
                :class="{
                  'bg-woot-50 dark:bg-woot-800': isTranslationsAttributesSynced,
                  'bg-gray-50 dark:bg-gray-700':
                    !isTranslationsAttributesSynced,
                }"
              >
                <woot-switch
                  :id="`message-template-link-sync-${currentLanguage}-switch`"
                  v-model="isTranslationsAttributesSynced"
                  class="message-template-link-sync-switch"
                  :label="
                    $t(
                      'CHATLYN_MESSAGE_TEMPLATE.CREATE_MODAL.SYNC_TRANSLATION_ATTRIBUTES'
                    )
                  "
                />
              </div>

              <language-bar
                has-color-mark
                :has-additional-text="false"
                :languages="languages"
                :translations="template.translations"
                :current-language="currentLanguage"
                @change="selectCurrentLanguage"
              />

              <template v-for="translation in template.translations">
                <!-- Temporarily hide header uploads for Phoenix list sends only -->
                <links-before-send-editor
                  v-if="
                    translation.status === 'approved' &&
                    getTranslationLinks(translation).links.length &&
                    hasLinks
                  "
                  v-show="translation.language === currentLanguage"
                  :key="translation.language + '-links-editor'"
                  :new-links="newLinks"
                  :language-links="getTranslationLinks(translation)"
                  :buttons-component="
                    getTranslationButtonsComponents(translation)
                  "
                  :is-translations-attributes-synced="
                    isTranslationsAttributesSynced
                  "
                  :current-language="currentLanguage"
                  :is-custom-media-required="false"
                  :hide-header-upload="shouldHideHeaderUpload"
                  @change-new-links="changeNewLinks"
                  @change-language-new-links="changeLanguageNewLinks"
                />

                <div
                  v-else
                  v-show="translation.language === currentLanguage"
                  :key="translation.language + '-no-links'"
                  class="message-template-links-message"
                >
                  <span> <fluent-icon icon="warning" :size="16" /> </span>

                  <span>
                    {{ $t('CHATLYN_MESSAGE_TEMPLATE.SEND_MODAL.NO_LINKS') }}
                  </span>
                </div>
              </template>
            </div>
          </template>
        </div>

        <div class="col-span-1 flex flex-col gap-3 lg:col-span-2">
          <div v-if="hasLinks">
            <div class="flex flex-row flex-nowrap items-center gap-1">
              <label>
                {{ $t('CHATLYN_MESSAGE_TEMPLATE.SEND_MODAL.RECIPIENTS') }}
              </label>

              <fluent-icon
                v-if="contactList && contactList.name"
                v-tooltip.top="
                  $t(
                    'CHATLYN_CONTACT_LISTS.SEND_MESSAGE_MODAL.RECIPIENTS_TOOLTIP',
                    { list: contactList.name }
                  )
                "
                icon="info"
                size="12"
              />
            </div>

            <div class="text-4xl font-light dark:text-white">
              {{ contactsCount }}
            </div>

            <total-count-warning v-if="showTotalCountWarning" />

            <send-message-limit-warning
              v-if="isSendMessageLimitShown"
              class="mt-0.5"
              :current-sending-limits="currentSendingLimits"
            />
          </div>

          <div class="message-template-number-container">
            <div class="flex items-center gap-1">
              <label>
                {{
                  $t(
                    'CHATLYN_MESSAGE_TEMPLATE.SEND_MODAL.SELECT_WHATSAPP_INBOX'
                  )
                }}
              </label>

              <fluent-icon
                v-tooltip.top="
                  $t('CHATLYN_CONTACT_LISTS.SEND_MESSAGE_MODAL.NUMBER_TOOLTIP')
                "
                icon="info"
                size="12"
              />
            </div>
            <input
              type="text"
              disabled
              :value="`${selectedWhatsAppInbox.name} (${selectedWhatsAppInboxPhoneNumber})`"
            />
          </div>

          <div>
            <label>
              {{ $t('CHATLYN_MESSAGE_TEMPLATE.MESSAGE_PREVIEW') }}
            </label>
            <language-bar
              class="mb-2"
              has-color-mark
              :has-additional-text="false"
              :languages="languages"
              :translations="template.translations"
              :current-language="currentLanguage"
              @change="
                (language) => {
                  if (!hasLinks) {
                    selectCurrentLanguage(language);
                  }
                }
              "
            />

            <template-message-preview
              v-if="messageTemplateTranslation"
              :current-translation="messageTemplateTranslation"
              :media-url="customFile"
              :empty-text="
                $t('CHATLYN_MESSAGE_TEMPLATE.SELECT_TEMPLATE_TO_PREVIEW')
              "
            />
          </div>
        </div>
      </div>
    </div>

    <div class="flex justify-end gap-2 px-8 pb-8 pt-0">
      <woot-button
        color-scheme="success"
        :disabled="
          isSubmitButtonDisabled ||
          hasErrors ||
          (isSpamConfirmRequired && !spamConfirmChecked)
        "
        :is-loading="isTemplateSending"
        :icon="showSchedule ? 'send-clock' : 'send'"
        @click.prevent="sendMessage"
      >
        {{
          $t(
            showSchedule
              ? 'CHATLYN_GENERAL.BUTTON.SEND_SCHEDULED'
              : 'CHATLYN_GENERAL.BUTTON.SEND'
          )
        }}
      </woot-button>
    </div>

    <div
      v-if="isMessageTemplateLoading"
      class="absolute left-0 top-0 z-10 flex h-full w-full items-center justify-center"
      style="background: rgba(0, 0, 0, 0.05)"
    >
      <span class="spinner h-8 w-8" />
    </div>
  </formulate-form>
</template>

<script>
import { format, getDate, getMinutes, setDate, setMinutes } from 'date-fns';
import { mapGetters } from 'vuex';
import TemplateMessagePreview from './TemplateMessagePreview';
import alertMixin from 'shared/mixins/alertMixin';
import MultiselectCh from '../../../../../shared/components/ui/Multiselect';
import { timeAfter } from '../../../../helper/formulateValidation';
import WootSwitch from 'shared/components/ui/Switch';
import getUuid from 'widget/helpers/uuid';
import WhatsAppAPI from '../../../../api/whatsApp';

import LinksBeforeSendEditor from './LinksBeforeSendEditor.vue';
import LanguageBar from './LanguageBar.vue';
import { timeZoneOptions } from '../../settings/inbox/helpers/businessHour';
import SlideAnimation from '../../../../components/animations/SlideAnimation.vue';
import SendMessageLimitWarning from './SendMessageLimitWarning.vue';
import TotalCountWarning from './components/TotalCountWarning.vue';
import { extractPhoenixParameters } from '../../../../helper/templateParameterExtractor';
import { buildSyntheticContact } from '../../contacts/helpers/inHouseHelpers';

// Above this many recipients, require an explicit spam-awareness confirmation
// before the send button is enabled.
const SPAM_CONFIRM_THRESHOLD = 250;

export default {
  name: 'SendMessageTemplateModal',
  components: {
    TemplateMessagePreview,
    MultiselectCh,
    WootSwitch,
    LinksBeforeSendEditor,
    LanguageBar,
    SlideAnimation,
    SendMessageLimitWarning,
    TotalCountWarning,
  },
  mixins: [alertMixin],
  props: {
    isContacts: {
      type: Boolean,
      default: false,
    },

    messageTemplate: {
      type: Object,
      default: () => {},
    },

    phones: {
      type: Array,
      default: () => [],
    },

    isInHousePhones: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      updateTineInterval: null,
      show: true,
      showSchedule: false,
      scheduleDate: null,
      scheduleTime: null,
      whatsAppInbox: null,
      contactList: {},
      template: {},
      currentDate: Date.now(),
      newLinks: [],
      currentLanguage: '',
      spamConfirmChecked: false,
      isTranslationsAttributesSynced: true,
      currentSendingLimits: 0,
      templateQuery: '',
      templatePage: 0,
      templateTotalPages: 0,
      templatePageSize: 25,
      templateFetchInFlight: null,
      isTemplateFetchingMore: false,
      templateInitialFetchDone: false,
      templateScrollHandler: null,
    };
  },
  computed: {
    ...mapGetters({
      managementAccount: 'management/getAccount',
      contactLists: 'contactLists/getAvailableLists',
      currentAccountId: 'getCurrentAccountId',
      contactListMeta: 'contactLists/getMeta',
      messageTemplates: 'messageTemplates/getApprovedMessageTemplates',
      isMessageTemplateLoading: 'messageTemplates/getIsMessageTemplateLoading',
      whatsAppInboxes: 'inboxes/getWhatsAppInboxes',
      currentContactList: 'contactLists/getList',
      isTemplateSending: 'messages/getIsSending',
      isPhoenix: 'accounts/isPhoenix',
    }),

    languages() {
      return this.template?.translations?.map((translation) => ({
        name: translation.language,
      }));
    },

    isShowWarning() {
      if (
        !Array.isArray(this.template.translations) ||
        !this.template.translations.length
      ) {
        return false;
      }

      const templateApprovedDate = new Date(
        Math.max(
          ...this.template.translations.map(
            (translation) =>
              (['approved', 'APPROVED'].includes(translation.status)
                ? new Date(
                    translation.statusUpdatedAt || translation.updatedAt
                  ).getTime()
                : -1) || -1
          )
        )
      );

      const currentDate = new Date();

      return (
        setMinutes(currentDate, getMinutes(currentDate) - 10).getTime() <=
        new Date(templateApprovedDate).getTime()
      );
    },

    isSubmitButtonDisabled() {
      if (this.isPhonesMode) {
        return (
          this.isTemplateSending ||
          !Object.keys(this.template).length ||
          !this.phones.length
        );
      }
      return this.isTemplateSending || this.isContacts
        ? !Object.keys(this.template).length
        : this.isContactListEmpty;
    },

    isContactListEmpty() {
      return !this.contactList || !Object.keys(this.contactList).length;
    },

    /**
     * True when the modal is invoked from the In-House view with a transient
     * list of phone rows. Switches the send pipeline to a synthetic-contact
     * payload (data.contacts[]) that bypasses the contactList lookup entirely.
     * Template selector + remote-fetch behaviour mirror the contacts-mode
     * branch. Decoupled from phones.length so an empty array does NOT silently
     * fall back to the legacy listId branch — submit is disabled instead.
     */
    isPhonesMode() {
      return !!this.isInHousePhones;
    },

    currentListId() {
      // eslint-disable-next-line no-underscore-dangle
      return this.contactList?._id || this.contactList?.id || null;
    },

    listFromStore() {
      return this.currentListId
        ? this.$store.getters['contactLists/getCurrentList'](this.currentListId)
        : null;
    },

    contactsCount() {
      if (this.isPhonesMode) {
        return this.phones.length;
      }

      if (this.isContactListEmpty) {
        return '--';
      }

      if (typeof this.listFromStore?.count === 'number') {
        return this.listFromStore.count;
      }

      // Only trust an authoritative total. A loaded `contacts` array may be a
      // single page, which would undercount and wrongly skip the spam gate
      // while sendMessage still targets the full list via listId.
      const fallbackCount = this.contactList?.count;

      return fallbackCount ?? '--';
    },

    showTotalCountWarning() {
      if (!this.isContacts) {
        return false;
      }

      // Show warning when we can't determine the exact total count
      // This means the template will be sent to all contacts but we can't show the exact number
      return typeof this.contactsCount !== 'number';
    },

    messageTemplateTranslation() {
      if (!this.template || !this.template.translations) {
        return {};
      }

      return this.template.translations.find(
        (translation) => translation.language === this.currentLanguage
      );
    },

    timezoneTitle() {
      const matchedTimeZone =
        timeZoneOptions()?.find(
          (timeZone) => timeZone.value === this.managementAccount?.timezone
        )?.label ||
        `${format(new Date(), 'z')}
        (${Intl.DateTimeFormat().resolvedOptions().timeZone})`;

      return `${this.$t(
        'CHATLYN_CONTACT_LISTS.SEND_MESSAGE_MODAL.TIMEZONE'
      )} ${matchedTimeZone}`;
    },

    minDate() {
      return format(this.currentDate, 'yyyy-MM-dd');
    },

    afterDate() {
      const date = new Date(this.currentDate);

      return format(setDate(date, getDate(date) - 1), 'yyyy-MM-dd');
    },

    minTime() {
      if (!this.scheduleDate) {
        return undefined;
      }

      if (format(this.currentDate, 'yyyy-MM-dd') !== this.scheduleDate) {
        return undefined;
      }

      const now = Date.now();

      return format(setMinutes(now, getMinutes(now) + 1), 'HH:mm');
    },

    selectedWhatsAppInbox() {
      return this.whatsAppInbox
        ? this.whatsAppInboxes.find(
            // eslint-disable-next-line eqeqeq
            (whatsAppInbox) => whatsAppInbox.id == this.whatsAppInbox
          )
        : this.whatsAppInboxes[0];
    },

    linksValueTypeOptions() {
      return ['ATTRIBUTES', 'STATIC_TEXT'].map((key) => ({
        label: this.$t(`CHATLYN_MESSAGE_TEMPLATE.ATTRIBUTES.${key}`),
        value: key.toLowerCase(),
      }));
    },

    buttonsComponent() {
      return this.messageTemplateTranslation.components.find(
        (component) => component.type === 'BUTTONS'
      );
    },

    headerComponent() {
      return this.messageTemplateTranslation?.components?.find(
        (component) => component.type === 'HEADER'
      );
    },

    headerHasMedia() {
      const headerFormat = this.headerComponent?.format;
      if (!headerFormat) {
        return false;
      }

      return ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(
        String(headerFormat).toUpperCase()
      );
    },

    shouldHideHeaderUpload() {
      return this.isPhoenixListSend && this.headerHasMedia;
    },

    customFile() {
      const link = this.currentLanguageLinks?.links?.find((l) =>
        ['image', 'video'].includes(l.valueType)
      );
      if (!link) return '';
      return link.previewUrl || link.value || '';
    },

    availableMessageTemplates() {
      return this.messageTemplates.filter(
        (template) => template.category !== 'CONSENT'
      );
    },

    isPhoenixListSend() {
      return this.isPhoenix && this.isContacts;
    },

    shouldUseRemoteTemplates() {
      return this.isPhoenixListSend || (this.isPhoenix && this.isPhonesMode);
    },

    hasMoreTemplates() {
      if (!this.shouldUseRemoteTemplates) {
        return false;
      }

      if (!this.templateTotalPages) {
        return this.templatePage === 0;
      }

      return this.templatePage < this.templateTotalPages;
    },

    isTemplateDropdownLoading() {
      if (!this.shouldUseRemoteTemplates) {
        return this.isMessageTemplateLoading;
      }

      if (!this.templateInitialFetchDone) {
        return this.isMessageTemplateLoading;
      }

      return this.isTemplateFetchingMore;
    },

    currentLanguageLinks: {
      get() {
        return this.newLinks.find(
          (newLinks) => newLinks.language === this.currentLanguage
        );
      },

      set(newValue) {
        let newLinks = JSON.parse(JSON.stringify(this.newLinks));

        const index = newLinks.findIndex(
          (newLink) => newLink.language === newValue.language
        );

        if (index !== -1) {
          newLinks[index] = newValue;
        }

        this.newLinks = newLinks;
      },
    },

    hasLinks() {
      if (!this.newLinks?.length) {
        return false;
      }

      return this.newLinks.some((links) => links.links.length);
    },

    isAllTranslationsApproved() {
      return !this.template?.translations?.some(
        (translation) => translation.status !== 'approved'
      );
    },

    selectedWhatsAppInboxPhoneNumber() {
      return (
        this.selectedWhatsAppInbox?.phone_number ||
        `+${this.selectedWhatsAppInbox?.additional_attributes?.number}`
      );
    },

    isSendMessageLimitShown() {
      if (typeof this.contactsCount !== 'number') {
        return false;
      }

      return (
        this.selectedWhatsAppInbox &&
        this.selectedWhatsAppInboxPhoneNumber &&
        this.contactsCount > this.currentSendingLimits
      );
    },

    /**
     * Large sends risk Meta spam protections. Require an explicit confirmation
     * when the recipient count is above the threshold, or when the exact count
     * can't be determined but a list is selected (could be very large).
     */
    isSpamConfirmRequired() {
      if (typeof this.contactsCount === 'number') {
        return this.contactsCount > SPAM_CONFIRM_THRESHOLD;
      }

      // Count not determinable (e.g. '--'): require confirmation whenever a list
      // is actually selected, regardless of which flow opened the modal — the
      // real size could be large. With nothing selected, Send is already
      // disabled, so no gate is needed.
      return !this.isContactListEmpty;
    },
  },
  watch: {
    isSpamConfirmRequired(newValue) {
      // Reset consent whenever the gate turns off so re-crossing the
      // threshold requires the user to confirm again.
      if (!newValue) {
        this.spamConfirmChecked = false;
      }
    },

    currentListId() {
      // Selecting a different list means a new recipient set, so any prior
      // spam acknowledgment no longer applies — require a fresh one.
      this.spamConfirmChecked = false;
    },

    shouldUseRemoteTemplates(newValue, oldValue) {
      if (!this.isContacts && !this.isPhonesMode) {
        return;
      }

      if (newValue && newValue !== oldValue) {
        this.initializeTemplateFetching();
      }
    },

    currentAccountId(newValue, oldValue) {
      if (!this.isContacts && !this.isPhonesMode) {
        return;
      }

      if (!newValue || newValue === oldValue) {
        return;
      }

      this.initializeTemplateFetching();
    },

    messageTemplate: {
      handler(newValue) {
        if (!newValue || !Object.keys(newValue).length) {
          return;
        }

        this.template = JSON.parse(JSON.stringify(newValue));
      },
      deep: true,
      immediate: true,
    },

    showSchedule: {
      handler(newValue) {
        if (newValue) {
          clearInterval(this.updateTineInterval);

          this.updateTineInterval = setInterval(() => {
            this.currentDate = Date.now();
          }, 1000 * 60);
        } else {
          clearInterval(this.updateTineInterval);
        }
      },
    },

    whatsAppInboxes: {
      handler(newValue) {
        if (!newValue?.length) {
          return;
        }

        this.setupMessageTemplateWhatsAppInbox();
      },
      immediate: true,
      deep: true,
    },

    'template._id': {
      handler(newValue) {
        if (!newValue) {
          return;
        }

        this.setupSelectedLinksValueTypes();

        if (this.template.translations.length) {
          this.currentLanguage = this.template.translations[0].language;
        }

        this.setupMessageTemplateWhatsAppInbox();
      },
      immediate: true,
    },

    selectedWhatsAppInboxPhoneNumber: {
      async handler(newValue) {
        if (!newValue) {
          return;
        }

        await this.getWhatsappSendingLimits();
      },
      immediate: true,
    },
  },
  mounted() {
    this.fetchManagementAccount();
    this.$store.dispatch('inboxes/get');

    if (this.isPhonesMode) {
      this.initializeTemplateFetching();
    } else if (this.isContacts) {
      this.contactList = this.currentContactList;
      this.initializeTemplateFetching();
    } else {
      this.fetchContactLists();
    }
  },
  beforeDestroy() {
    clearInterval(this.updateTineInterval);
    this.removeTemplateScrollHandler();
  },
  methods: {
    extractPhoenixParameters,
    timeAfter,

    selectCurrentLanguage(newValue) {
      this.currentLanguage = newValue;
    },

    onClose() {
      this.$emit('close');
    },

    getTranslationButtonsComponents(translation) {
      return translation?.components?.find(
        (component) => component.type === 'BUTTONS'
      );
    },

    getTranslationLinks(translation) {
      return this.newLinks.find(
        (newLinks) => newLinks.language === translation.language
      );
    },

    async fetchTemplates({ append = false } = {}) {
      if (!this.currentAccountId) {
        return null;
      }

      if (!this.shouldUseRemoteTemplates) {
        await this.$store.dispatch('messageTemplates/getAllMessageTemplates', {
          accountId: this.currentAccountId,
          requestParams: {
            status: 'approved',
          },
        });
        return null;
      }

      if (
        append &&
        (this.isTemplateFetchingMore ||
          this.isMessageTemplateLoading ||
          !this.hasMoreTemplates)
      ) {
        return null;
      }

      if (this.templateFetchInFlight && !append) {
        await this.templateFetchInFlight;
      }

      const nextPage = append ? this.templatePage + 1 : 1;

      if (
        append &&
        this.templateTotalPages &&
        nextPage > this.templateTotalPages
      ) {
        return null;
      }

      const trimmedQuery = (this.templateQuery || '').trim();
      const requestParams = {
        status: 'approved',
        page: nextPage,
        limit: this.templatePageSize,
      };

      if (trimmedQuery) {
        requestParams.search = trimmedQuery;
      }

      if (append) {
        requestParams.append = true;
        this.isTemplateFetchingMore = true;
      } else {
        this.templatePage = 0;
        this.templateTotalPages = 0;
        this.templateInitialFetchDone = false;
      }

      const fetchPromise = this.$store
        .dispatch('messageTemplates/getAllMessageTemplates', {
          accountId: this.currentAccountId,
          requestParams,
        })
        .then((data) => {
          if (data && data.totalPages !== undefined) {
            this.templateTotalPages = data.totalPages;
          }

          if (append) {
            this.templatePage = nextPage;
          } else {
            this.templatePage = nextPage;
            this.templateInitialFetchDone = true;
          }

          return data;
        })
        .finally(() => {
          if (append) {
            this.isTemplateFetchingMore = false;
          }

          if (this.templateFetchInFlight === fetchPromise) {
            this.templateFetchInFlight = null;
          }
        });

      this.templateFetchInFlight = fetchPromise;

      return fetchPromise;
    },

    async fetchContactLists() {
      await this.$store.dispatch('contactLists/getLists', {
        accountId: this.$route.params.accountId,
      });
    },

    changeNewLinks(newValue) {
      this.newLinks = structuredClone(newValue);
    },

    changeLanguageNewLinks(newValue) {
      this.currentLanguageLinks = structuredClone(newValue);
    },

    initializeTemplateFetching() {
      if (!this.isContacts && !this.isPhonesMode) {
        return;
      }

      if (this.shouldUseRemoteTemplates) {
        this.resetTemplatePagination();
        this.fetchTemplates({ append: false });
      } else {
        this.fetchTemplates();
      }
    },

    resetTemplatePagination() {
      this.templateQuery = '';
      this.templatePage = 0;
      this.templateTotalPages = 0;
      this.templateFetchInFlight = null;
      this.isTemplateFetchingMore = false;
      this.templateInitialFetchDone = false;
      this.removeTemplateScrollHandler();
    },

    handleTemplateDropdownOpen() {
      if (!this.shouldUseRemoteTemplates) {
        return;
      }

      this.$nextTick(() => {
        this.attachTemplateScrollHandler();
      });
    },

    handleTemplateDropdownClose() {
      this.removeTemplateScrollHandler();
    },

    handleTemplateSearch(query) {
      if (!this.shouldUseRemoteTemplates) {
        return;
      }

      this.templateQuery = query || '';
      this.fetchTemplates({ append: false });
    },

    attachTemplateScrollHandler() {
      if (!this.hasMoreTemplates) {
        return;
      }

      this.removeTemplateScrollHandler();

      const dropdownEl = this.$refs.templateSelect?.$el;
      if (!dropdownEl) {
        return;
      }

      const scrollContainer = dropdownEl.querySelector(
        '.multiselect__content-wrapper'
      );
      if (!scrollContainer) {
        return;
      }

      const handler = (event) => {
        const el = event.target;
        const threshold = 40;

        if (el.scrollHeight - el.scrollTop - el.clientHeight <= threshold) {
          this.fetchTemplates({ append: true });
        }
      };

      scrollContainer.addEventListener('scroll', handler);
      this.templateScrollHandler = { container: scrollContainer, handler };
    },

    removeTemplateScrollHandler() {
      if (!this.templateScrollHandler) {
        return;
      }

      const { container, handler } = this.templateScrollHandler;
      container.removeEventListener('scroll', handler);
      this.templateScrollHandler = null;
    },

    async sendMessage() {
      // Phoenix: if a local header media file is selected, upload it first to get a media ID
      try {
        if (this.isPhoenix && Array.isArray(this.newLinks)) {
          const uploadTasks = this.newLinks.reduce((tasks, languageLinks) => {
            const headerMediaLink = (languageLinks.links || []).find(
              (link) =>
                link.linkType === 'header' &&
                ['image', 'video', 'document'].includes(link.valueType)
            );

            if (!headerMediaLink || !headerMediaLink.file) {
              return tasks;
            }

            const uploadPromise = WhatsAppAPI.uploadMediaFile({
              phoneNumber: this.selectedWhatsAppInboxPhoneNumber,
              file: headerMediaLink.file,
              type: headerMediaLink.valueType,
              filename: headerMediaLink.file?.name,
            }).then((response) => {
              if (response?.data?.id) {
                headerMediaLink.value = response.data.id; // replace with WhatsApp media ID
              }
            });

            tasks.push(uploadPromise);
            return tasks;
          }, []);

          if (uploadTasks.length) {
            await Promise.all(uploadTasks);
          }
        }
      } catch (e) {
        // Silent fallback; if upload fails we'll try to use example/URL below
      }

      const links = this.template.translations.map((translation) => {
        const matchedLinks = this.newLinks.find(
          (newLink) => translation.language === newLink.language
        );

        const linksToSend = {
          language: translation.language,
          body: [],
          buttons: [],
          header: [],
        };

        ['header', 'body', 'buttons'].forEach((key) => {
          [...matchedLinks.links].forEach((newLink) => {
            if (newLink.linkType === key) {
              if (this.isPhoenixListSend && key === 'header') {
                return;
              }

              if (
                ['image', 'video'].includes(newLink.valueType) &&
                !newLink.value
              ) {
                newLink.value = newLink.example;
              }

              delete newLink.linkType;
              delete newLink.valueType;
              delete newLink.id;
              // cleanup preview-only fields
              delete newLink.file;
              delete newLink.previewUrl;

              linksToSend[key].push(newLink);
            }
          });

          if (!linksToSend[key].length) {
            delete linksToSend[key];
          }
        });

        return linksToSend;
      });

      const baseData = {
        clientId: this.currentAccountId,
        date: this.showSchedule
          ? new Date(`${this.scheduleDate} ${this.scheduleTime}`)
          : new Date(),
        number: this.selectedWhatsAppInboxPhoneNumber.replace(/\+/g, ''),
        links: links,
      };

      if (this.isPhoenix) {
        baseData.templateName = this.template.name;
      } else {
        // eslint-disable-next-line no-underscore-dangle
        baseData.templateId = this.template._id;
      }

      if (this.isPhonesMode) {
        const fallbackLanguage =
          links?.[0]?.language ||
          this.template?.translations?.[0]?.language ||
          'en';
        const contacts = this.phones.map((row) => {
          const phoneNumber = (row.phone_number || '').startsWith('+')
            ? row.phone_number
            : `+${row.phone_number}`;
          return buildSyntheticContact(
            { ...row, phone_number: phoneNumber },
            fallbackLanguage,
            this.currentAccountId
          );
        });

        try {
          await this.$store.dispatch('messages/sendMessage', {
            data: { ...baseData, contacts },
          });
          this.showAlert(
            this.$t(
              'CHATLYN_MESSAGE_TEMPLATE.SEND_MODAL.SUCCESS_MESSAGE.ALL_MESSAGE_SENT'
            )
          );
        } catch (_e) {
          this.showAlert(
            this.$t(
              'CHATLYN_MESSAGE_TEMPLATE.SEND_MODAL.ERROR_MESSAGE.NO_ATTRIBUTES_MESSAGE'
            )
          );
        } finally {
          this.onClose();
        }
        return;
      }

      try {
        const payload = {
          data: {
            ...baseData,
            // eslint-disable-next-line no-underscore-dangle
            listId: this.contactList._id || this.contactList.id,
          },
        };

        const result = await this.$store.dispatch(
          'messages/sendMessage',
          payload
        );

        if (result?.status === 206) {
          this.showAlert(
            this.$t(
              'CHATLYN_MESSAGE_TEMPLATE.SEND_MODAL.ERROR_MESSAGE.NO_SOME_ATTRIBUTES_MESSAGE'
            )
          );

          return;
        }

        this.showAlert(
          this.$t(
            'CHATLYN_MESSAGE_TEMPLATE.SEND_MODAL.SUCCESS_MESSAGE.ALL_MESSAGE_SENT'
          )
        );
      } catch (error) {
        this.showAlert(
          this.$t(
            'CHATLYN_MESSAGE_TEMPLATE.SEND_MODAL.ERROR_MESSAGE.NO_ATTRIBUTES_MESSAGE'
          )
        );
      } finally {
        this.onClose();
      }
    },

    setupSelectedLinksValueTypes() {
      // Change it if we enable multi language templates

      const newLinks = this.template.translations.map((translation) => {
        const newLanguageLinks = [];

        // Phoenix-only shortcut: when no links are present, derive from components
        const isPhoenixTemplate =
          this.isPhoenix && translation.components && !translation.links;

        let links;
        if (isPhoenixTemplate) {
          links = this.extractPhoenixParameters(translation);
          if (this.isPhoenixListSend) {
            links.header = [];
          }
        } else {
          links = translation.links
            ? JSON.parse(JSON.stringify(translation.links))
            : { header: [], body: [], buttons: [] };

          const editableHeaderFormats = ['image', 'video'];
          const headerComponent = translation.components?.find(
            (component) => component.type === 'HEADER'
          );
          if (
            headerComponent &&
            editableHeaderFormats.includes(
              String(headerComponent.format || '').toLowerCase()
            ) &&
            !this.isPhoenixListSend
          ) {
            links.header.push({
              example: headerComponent.example?.header_handle?.[0] || '',
              number: 1,
              value: '',
              valueType: String(headerComponent.format || '').toLowerCase(),
            });
          }
        }

        ['header', 'body', 'buttons'].forEach((key) => {
          links[key].forEach((link) => {
            const newLink = {
              ...link,
              linkType: key,
              id: getUuid(),
            };

            if (
              this.isPhoenix &&
              this.isContacts &&
              key === 'header' &&
              ['image', 'video', 'document'].includes(newLink.valueType) &&
              !newLink.value &&
              newLink.example
            ) {
              newLink.value = newLink.example;
            }

            if (!newLink.valueType) {
              newLink.valueType = link.value
                ? this.linksValueTypeOptions[0].value
                : this.linksValueTypeOptions[1].value;
            }

            newLanguageLinks.push(newLink);
          });
        });

        return {
          language: translation.language,
          links: newLanguageLinks,
        };
      });

      this.newLinks = newLinks;
    },

    setupMessageTemplateWhatsAppInbox() {
      if (
        !this.template ||
        !Object.keys(this.template).length ||
        !this.whatsAppInboxes?.length
      ) {
        return;
      }

      const matchedWhatsAppInbox =
        this.whatsAppInboxes.find(
          (whatsAppInbox) =>
            whatsAppInbox?.phone_number?.replace(/\+/g, '') ===
              this.template.phoneNumber ||
            whatsAppInbox?.additional_attributes?.number ===
              this.template.phoneNumber
        ) || this.whatsAppInboxes[0];

      this.whatsAppInbox = matchedWhatsAppInbox.id;
    },

    async fetchManagementAccount() {
      await this.$store.dispatch('management/getAccount', {
        accountId: this.currentAccountId,
      });
    },

    async getWhatsappSendingLimits() {
      try {
        const isPhoenix = this.$store.getters['accounts/isPhoenix'];

        let response;
        if (isPhoenix) {
          response = await WhatsAppAPI.getPhoenixSendingLimits(
            this.selectedWhatsAppInboxPhoneNumber,
            this.currentAccountId
          );
          this.currentSendingLimits = response.data.currentLimitNumber;
        } else {
          response = await WhatsAppAPI.getWhatsApp24hSendingLimits(
            this.selectedWhatsAppInboxPhoneNumber
          );
          this.currentSendingLimits = response.data;
        }
      } catch (error) {
        //
      }
    },
  },
};
</script>

<style lang="scss">
.message-template-links-message {
  @apply flex flex-row items-center gap-2 text-xs;
}

.send-message-template-modal-body {
  @apply flex w-full flex-col flex-nowrap;

  .formulate-input-label {
    @apply mb-2;
  }

  .message-template-scheduled-container {
    @apply flex flex-col flex-nowrap gap-3 rounded-md border p-3 dark:border-gray-700;
  }
}
</style>
